/**
 * Les bulles « première fois » : quand on découvre un geste, l'interface aide une fois, là
 * où ça se passe — puis plus jamais.
 *
 * Une seule bulle à la fois : les suivantes attendent leur tour (un geste en débloque
 * parfois deux). Une bulle fermée — par son bouton, le geste, Échap, ou seule au bout de
 * 7 s — est vue pour de bon, sur le compte : POST /hints/:key/seen,
 * et la page reçoit la liste dans <meta name="seen-hints"> — une seule source, ce que le
 * compte dit fait foi. Une bulle dont l'ancre a disparu, ou dont le marqueur est retiré,
 * n'est pas vue : elle reviendra.
 *
 * Déclarées dans les vues par le contrôleur `coach` ; les clés et les textes vivent dans
 * les locales (`coach_marks.*`).
 */
import type { Placement } from "@floating-ui/dom";
import { Bubble, type BubbleAnchor } from "./bubble";

export interface CoachMark {
  /** `editor.group`, `editor.arrangement`… */
  key: string
  /** L'ancre au moment de se montrer (null : pas là, la bulle passe son tour). */
  anchor: () => BubbleAnchor | null
  text: string
  placement?: Placement
  /** Le bouton qui ferme la bulle. */
  dismissLabel?: string
}

/** Entre deux bulles : la suivante n'arrive pas dans la foulée de la fermeture. */
const NEXT_DELAY = 400;
/** Une bulle qu'on ne ferme pas se ferme seule (editor-round-2 § 5) — et compte comme vue :
 *  elle a eu son temps à l'écran, elle ne reviendra pas gêner. */
const AUTO_DISMISS = 7000;

let seen: Set<string> | null = null;
let current: { mark: CoachMark; bubble: Bubble; release: () => void } | null = null;
const queue: CoachMark[] = [];

/** Les clés déjà vues : celles du compte, et celles fermées depuis le chargement de la page. */
function seenKeys(): Set<string> {
  if (seen) return seen;
  seen = new Set<string>();
  const meta = document.querySelector<HTMLMetaElement>('meta[name="seen-hints"]');
  try {
    for (const key of JSON.parse(meta?.content || "[]")) seen.add(String(key));
  } catch { /* une liste illisible ne cache rien */ }
  return seen;
}

export function hasSeenHint(key: string): boolean {
  return seenKeys().has(key);
}

/** Demande à montrer une bulle : maintenant, ou à son tour — jamais si elle a été vue. */
export function requestCoachMark(mark: CoachMark): void {
  if (hasSeenHint(mark.key) || current?.mark.key === mark.key || queue.some((m) => m.key === mark.key)) return;
  if (current) queue.push(mark);
  else show(mark);
}

/** La bulle est vue : fermée, retenue, la suivante viendra. */
export function dismissCoachMark(key: string): void {
  markSeen(key);
  if (current?.mark.key !== key) return;
  close();
  setTimeout(next, NEXT_DELAY);
}

/** La bulle n'a plus lieu d'être (marqueur retiré) : retirée sans être vue. */
export function withdrawCoachMark(key: string): void {
  const index = queue.findIndex((m) => m.key === key);
  if (index >= 0) queue.splice(index, 1);
  if (current?.mark.key !== key) return;
  close();
  next();
}

function show(mark: CoachMark): void {
  const anchor = mark.anchor();
  if (!anchor) {
    next();
    return;
  }
  const bubble = new Bubble(anchor, {
    kind: "coach",
    text: mark.text,
    placement: mark.placement ?? "top",
    dismissLabel: mark.dismissLabel,
    onDismiss: () => dismissCoachMark(mark.key),
  });

  // Faire le geste expliqué vaut « compris » ; Échap ferme aussi ; le temps qui passe aussi
  const onAnchorClick = () => dismissCoachMark(mark.key);
  const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") dismissCoachMark(mark.key); };
  if (anchor instanceof Element) anchor.addEventListener("click", onAnchorClick);
  document.addEventListener("keydown", onKey);
  const timer = setTimeout(() => dismissCoachMark(mark.key), AUTO_DISMISS);
  const release = () => {
    if (anchor instanceof Element) anchor.removeEventListener("click", onAnchorClick);
    document.removeEventListener("keydown", onKey);
    clearTimeout(timer);
  };

  current = { mark, bubble, release };
}

function close(): void {
  if (!current) return;
  current.release();
  current.bubble.dismiss();
  current = null;
}

function next(): void {
  const mark = queue.shift();
  if (mark && !hasSeenHint(mark.key)) show(mark);
  else if (mark) next();
}

function markSeen(key: string): void {
  const keys = seenKeys();
  if (keys.has(key)) return;
  keys.add(key);

  const token = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;
  fetch(`/hints/${encodeURIComponent(key)}/seen`, {
    method: "POST",
    headers: token ? { "X-CSRF-Token": token } : {},
    credentials: "same-origin",
    keepalive: true,
  }).catch(() => { /* retenue pour cette page ; au pire, elle reviendra une fois */ });
}

/** Tests : repart d'un état vierge. */
export function resetCoachMarks(): void {
  close();
  queue.length = 0;
  seen = null;
}
